import { Box, Button, Typography, SxProps } from '@mui/material';
import { code, descText, errorText, infoText, warningBox } from 'app/components/AsanImzaComponent/ConfirmAsanImza.sx';
import WarningIcon from 'app/components/Icons/WarningIcon';
import BackButton from 'app/components/BackButton';
import { Dispatch, SetStateAction } from 'react';
import useConfirmAsanImza from 'app/components/AsanImzaComponent/useConfirmAsanImza';
import CountdownTimer from 'app/components/CountdownTimer';
import SuccessModal from 'app/components/SuccessModal';
import { useSendToSign } from 'app/utils/services/transferService';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';
import { useLayoutContext } from 'app/storage/LayoutContext';
import { useSendSalaryToCsi } from 'app/utils/services/salaryServices';
import { useLocation } from 'react-router-dom';

interface Props {
  verifyCode?: string;
  asanModalType?: string;
  btnSx?: SxProps;
  close?: Dispatch<SetStateAction<boolean>>;
  timerTime?: any;
  pathUrl?: any;
  businessKey?: any;
  isRegistration?: boolean;
  title?: string;
  redirectUrl?: string;
  isClosedAsanModal?: boolean;
}
const AsanImza = (props: Props) => {
  const {
    verifyCode,
    asanModalType,
    btnSx,
    close,
    timerTime,
    pathUrl,
    businessKey,
    isRegistration = false,
    title,
    redirectUrl,
    isClosedAsanModal,
  } = props;
  const {
    t,
    handleRepeatCode,
    dataAsanImza,
    data,
    handleCancel,
    successModal,
    setSuccessModal,
    errorModal,
    setErrorModal,
  } = useConfirmAsanImza({
    timer: timerTime,
    pathUrl,
    businessKey,
    asanModalType,
    isRegistration,
  });

  const navigate = useCustomNavigate();

  const { sendToSignMutation, dataSendToSign, isLoadingSendToSign } = useSendToSign();

  const sendToCsi = useSendSalaryToCsi();

  const { layoutState, setLayoutState } = useLayoutContext();

  const location = useLocation();

  const handleSuccessModalAction = (btnType: string) => {
    sendToCsi.mutate(
      {
        req_model: { form_id: btnType },
        pathUrl: pathUrl.split('/')[4],
        business_key: dataAsanImza?.business_key,
      },
      {
        onSuccess: (resp) => {
          if (resp.form_id !== 'csi') {
            navigate(`${resp.form_id}`);
          } else {
            setLayoutState({
              ...layoutState,
              csiOpen: true,
              csiAction: (rating: number, comment: string) => {
                sendToCsi.mutate(
                  {
                    req_model: { rating, comment },
                    pathUrl: pathUrl.split('/')[4],
                    business_key: resp.business_key,
                  },
                  {
                    onSuccess: (subResp) => {
                      setLayoutState({ ...layoutState, csiOpen: false });
                      navigate(`${subResp.form_id}`);
                    },
                  }
                );
              },
            });
          }
        },
      }
    );

    const pathSegments = location.pathname.split('/');
    const page = pathSegments[pathSegments.length - 1];

    setLayoutState({
      ...layoutState,
      csiRedirectPage: btnType === 'go_home' ? 'home-page' : page,
    });
  };

  if (successModal) {
    return (
      <SuccessModal
        successText={t('commonTerms.paymentSigned')}
        open={successModal}
        close={setSuccessModal}
        sign={true}
        closeAsan={close}
        title={title}
        redirectUrl={redirectUrl ?? dataAsanImza?.redirect_url}
        handleAction={
          pathUrl.split('/')[4] === 'transfer-page' || pathUrl.split('/')[4] === 'payment_salary-make_payment-confirm'
            ? handleSuccessModalAction
            : null
        }
      />
    );
  } else if (errorModal) {
    return (
      <SuccessModal
        successText={t('commonTerms.paymentNotSigned')}
        open={errorModal}
        close={setErrorModal}
        sign={false}
        unsuccess={true}
        closeAsan={close}
        isClosedAsanModal={isClosedAsanModal}
      />
    );
  } else {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
        <Box>
          {asanModalType !== 'transfer' && <Typography sx={infoText}>{t('confirm.confirmAsanImza')}</Typography>}
          <Typography sx={descText}>{t('confirm.asanImzaDesc')}</Typography>
          <Typography sx={code}>
            {data?.verification_code !== undefined ? data?.verification_code : verifyCode}
          </Typography>
          <CountdownTimer
            repeat={handleRepeatCode}
            timerTime={data?.timer !== undefined ? data?.timer : timerTime}
            errorCase={dataAsanImza?.result === 'error'}
          />
          {dataAsanImza?.error && (
            <Box sx={warningBox}>
              <WarningIcon sx={{ mr: '16px' }} />
              <Typography sx={errorText}>{dataAsanImza?.error && dataAsanImza?.error.text}</Typography>
            </Box>
          )}
        </Box>
        {asanModalType === 'transfer' ? (
          <Button
            sx={btnSx}
            color={'secondary'}
            onClick={() => {
              handleCancel();
              close(false);
            }}
          >
            {t('commonActions.close')}
          </Button>
        ) : (
          <BackButton btnSx={btnSx} text={'confirm.back'} asan={true} cancel={handleCancel} />
        )}
      </Box>
    );
  }
};

export default AsanImza;
